import Phaser from 'phaser';
import { getVehicle, VEHICLES } from '../config/vehicles';

function canvasTexture(
  scene: Phaser.Scene,
  key: string,
  w: number,
  h: number,
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
): void {
  if (scene.textures.exists(key)) return;
  const canvas = scene.textures.createCanvas(key, w, h);
  if (!canvas) return;
  const ctx = canvas.getContext();
  draw(ctx, w, h);
  canvas.refresh();
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

function hex(n: number): string {
  return `#${n.toString(16).padStart(6, '0')}`;
}

const FALLBACK_PALETTE: Record<string, [number, number, number]> = {
  'nairobi-legend': [0x111111, 0xffb703, 0xff6b1a],
  'neon-beast': [0x5a189a, 0x00f5d4, 0x240046],
  'mtaa-express': [0xffd60a, 0xd00000, 0x111111],
  'coastal-cruiser': [0x00b4d8, 0xffffff, 0xff6b6b],
  shujaa: [0x1b4332, 0xd4a373, 0x081c15],
  'midnight-king': [0x14213d, 0xff006e, 0xc0c0c0],
};

function drawPremiumMatatu(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  vehicleId: string,
): void {
  const vehicle = getVehicle(vehicleId);
  const [primary, secondary, accent] =
    FALLBACK_PALETTE[vehicle.id] ?? FALLBACK_PALETTE['nairobi-legend']!;

  ctx.clearRect(0, 0, w, h);

  // Soft ground shadow
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.ellipse(w / 2, h - 8, w * 0.36, 7, 0, 0, Math.PI * 2);
  ctx.fill();

  // Body
  const bx = 14;
  const by = 16;
  const bw = w - 28;
  const bh = h - 36;
  const grad = ctx.createLinearGradient(bx, by, bx + bw, by);
  grad.addColorStop(0, hex(primary));
  grad.addColorStop(0.45, hex(primary));
  grad.addColorStop(1, hex(accent));
  roundRect(ctx, bx, by, bw, bh, 14);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.18)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Roof shine
  const shine = ctx.createLinearGradient(bx, by, bx, by + bh * 0.4);
  shine.addColorStop(0, 'rgba(255,255,255,0.22)');
  shine.addColorStop(1, 'rgba(255,255,255,0)');
  roundRect(ctx, bx + 4, by + 4, bw - 8, bh * 0.35, 10);
  ctx.fillStyle = shine;
  ctx.fill();

  // Decorative roof graphic
  ctx.save();
  roundRect(ctx, bx + 8, by + bh * 0.28, bw - 16, bh * 0.42, 8);
  ctx.clip();
  ctx.fillStyle = hex(secondary);
  ctx.globalAlpha = 0.9;
  if (vehicle.id === 'neon-beast') {
    ctx.strokeStyle = hex(secondary);
    ctx.lineWidth = 3;
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(w * 0.3, by + bh * 0.3 + i * 14);
      ctx.lineTo(w * 0.5, by + bh * 0.38 + i * 14);
      ctx.lineTo(w * 0.7, by + bh * 0.3 + i * 14);
      ctx.stroke();
    }
  } else if (vehicle.id === 'mtaa-express') {
    for (let i = -h; i < w + h; i += 14) {
      ctx.beginPath();
      ctx.moveTo(i, by + bh * 0.28);
      ctx.lineTo(i + 40, by + bh * 0.7);
      ctx.lineTo(i + 28, by + bh * 0.7);
      ctx.lineTo(i - 12, by + bh * 0.28);
      ctx.fill();
    }
  } else {
    for (let i = 0; i < 6; i++) {
      const y = by + bh * 0.32 + i * 12;
      ctx.beginPath();
      ctx.moveTo(w / 2, y);
      ctx.lineTo(w / 2 - 22 + i * 2, y + 10);
      ctx.lineTo(w / 2 + 22 - i * 2, y + 10);
      ctx.closePath();
      ctx.fill();
    }
  }
  ctx.restore();
  ctx.globalAlpha = 1;

  // Windshield (front = top)
  roundRect(ctx, bx + 10, by + 10, bw - 20, 28, 6);
  const glass = ctx.createLinearGradient(0, by + 10, 0, by + 38);
  glass.addColorStop(0, '#9ecae8');
  glass.addColorStop(1, '#234e70');
  ctx.fillStyle = glass;
  ctx.fill();
  ctx.strokeStyle = '#0b1320';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Rear window
  roundRect(ctx, bx + 14, by + bh - 34, bw - 28, 18, 4);
  ctx.fillStyle = '#3d5a80';
  ctx.fill();

  // Headlights
  ctx.fillStyle = '#fff3b0';
  roundRect(ctx, bx + 8, by + 4, 12, 8, 2);
  roundRect(ctx, bx + bw - 20, by + 4, 12, 8, 2);
  ctx.fill();

  // Taillights
  ctx.fillStyle = '#ef233c';
  roundRect(ctx, bx + 10, by + bh - 10, 14, 6, 2);
  roundRect(ctx, bx + bw - 24, by + bh - 10, 14, 6, 2);
  ctx.fill();

  // Wheels
  const drawWheel = (cx: number, cy: number) => {
    ctx.fillStyle = '#111';
    ctx.beginPath();
    ctx.arc(cx, cy, 9, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = hex(secondary);
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fill();
  };
  drawWheel(bx - 2, by + 36);
  drawWheel(bx + bw + 2, by + 36);
  drawWheel(bx - 2, by + bh - 28);
  drawWheel(bx + bw + 2, by + bh - 28);

  // Side mirrors
  ctx.fillStyle = '#222';
  roundRect(ctx, bx - 6, by + 22, 8, 6, 2);
  roundRect(ctx, bx + bw - 2, by + 22, 8, 6, 2);
  ctx.fill();
}

function drawCar(ctx: CanvasRenderingContext2D, w: number, h: number, color: string): void {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = 'rgba(0,0,0,0.28)';
  ctx.beginPath();
  ctx.ellipse(w / 2, h - 5, w * 0.34, 5, 0, 0, Math.PI * 2);
  ctx.fill();

  const bx = 8;
  const by = 12;
  const bw = w - 16;
  const bh = h - 26;
  const body = ctx.createLinearGradient(bx, by, bx + bw, by);
  body.addColorStop(0, color);
  body.addColorStop(0.5, color);
  body.addColorStop(1, '#111111');
  roundRect(ctx, bx, by, bw, bh, 9);
  ctx.fillStyle = body;
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Windshield (front = top)
  roundRect(ctx, bx + 6, by + 8, bw - 12, 18, 4);
  const glass = ctx.createLinearGradient(0, by + 8, 0, by + 26);
  glass.addColorStop(0, '#bde0fe');
  glass.addColorStop(1, '#3a5a78');
  ctx.fillStyle = glass;
  ctx.fill();

  // Roof highlight strip
  ctx.fillStyle = 'rgba(255,255,255,0.15)';
  roundRect(ctx, bx + 8, by + 30, bw - 16, bh * 0.28, 4);
  ctx.fill();

  // Lights
  ctx.fillStyle = '#ffe66d';
  roundRect(ctx, bx + 4, by + 2, 10, 5, 2);
  roundRect(ctx, bx + bw - 14, by + 2, 10, 5, 2);
  ctx.fill();
  ctx.fillStyle = '#ef233c';
  roundRect(ctx, bx + 6, by + bh - 8, 10, 5, 2);
  roundRect(ctx, bx + bw - 16, by + bh - 8, 10, 5, 2);
  ctx.fill();

  // Wheels
  ctx.fillStyle = '#111';
  ctx.beginPath();
  ctx.arc(bx - 1, by + 28, 7, 0, Math.PI * 2);
  ctx.arc(bx + bw + 1, by + 28, 7, 0, Math.PI * 2);
  ctx.arc(bx - 1, by + bh - 18, 7, 0, Math.PI * 2);
  ctx.arc(bx + bw + 1, by + bh - 18, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ced4da';
  ctx.beginPath();
  ctx.arc(bx - 1, by + 28, 2.5, 0, Math.PI * 2);
  ctx.arc(bx + bw + 1, by + 28, 2.5, 0, Math.PI * 2);
  ctx.arc(bx - 1, by + bh - 18, 2.5, 0, Math.PI * 2);
  ctx.arc(bx + bw + 1, by + bh - 18, 2.5, 0, Math.PI * 2);
  ctx.fill();
}

/** Obstacle / collectible textures + optional vehicle fallbacks. */
export function generateFallbackTextures(scene: Phaser.Scene, onlyVehicleId?: string): void {
  if (onlyVehicleId) {
    const v = getVehicle(onlyVehicleId);
    canvasTexture(scene, v.spriteKey, 128, 200, (ctx, w, h) =>
      drawPremiumMatatu(ctx, w, h, v.id),
    );
    return;
  }

  for (const v of VEHICLES) {
    if (!scene.textures.exists(v.spriteKey)) {
      canvasTexture(scene, v.spriteKey, 128, 200, (ctx, w, h) =>
        drawPremiumMatatu(ctx, w, h, v.id),
      );
    }
  }

  canvasTexture(scene, 'car-red', 56, 90, (ctx, w, h) => drawCar(ctx, w, h, '#c1121f'));
  canvasTexture(scene, 'car-blue', 56, 90, (ctx, w, h) => drawCar(ctx, w, h, '#0077b6'));
  canvasTexture(scene, 'car-yellow', 56, 90, (ctx, w, h) => drawCar(ctx, w, h, '#ffb703'));
  canvasTexture(scene, 'truck', 64, 120, (ctx, w, h) => {
    drawCar(ctx, w, h, '#495057');
    ctx.fillStyle = '#adb5bd';
    roundRect(ctx, 12, 18, w - 24, 36, 4);
    ctx.fill();
  });

  canvasTexture(scene, 'boda', 40, 70, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#212529';
    ctx.fillRect(w / 2 - 4, 18, 8, 36);
    ctx.beginPath();
    ctx.arc(w / 2, 16, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ff006e';
    ctx.fillRect(w / 2 - 12, 28, 24, 8);
    ctx.fillStyle = '#111';
    ctx.beginPath();
    ctx.arc(w / 2, 14, 7, 0, Math.PI * 2);
    ctx.arc(w / 2, 56, 9, 0, Math.PI * 2);
    ctx.fill();
  });

  canvasTexture(scene, 'pothole', 54, 36, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#1b1b1b';
    ctx.beginPath();
    ctx.ellipse(w / 2, h / 2, w * 0.42, h * 0.38, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#3d3d3d';
    ctx.beginPath();
    ctx.ellipse(w / 2 - 4, h / 2 - 2, w * 0.22, h * 0.18, 0, 0, Math.PI * 2);
    ctx.fill();
  });

  canvasTexture(scene, 'cones', 48, 40, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    const drawCone = (x: number) => {
      ctx.fillStyle = '#fb8500';
      ctx.beginPath();
      ctx.moveTo(x, 8);
      ctx.lineTo(x + 10, h - 6);
      ctx.lineTo(x - 10, h - 6);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.fillRect(x - 7, h - 18, 14, 5);
    };
    drawCone(14);
    drawCone(34);
  });

  canvasTexture(scene, 'fare', 36, 36, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#ffd166';
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, 14, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#bc6c25';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.fillStyle = '#7f4f24';
    ctx.font = 'bold 12px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('KSh', w / 2, h / 2 + 1);
  });

  canvasTexture(scene, 'bonus', 40, 40, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#06d6a0';
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, 15, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('★', w / 2, h / 2 + 1);
  });

  canvasTexture(scene, 'building', 64, 120, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#264653';
    ctx.fillRect(4, 10, w - 8, h - 10);
    ctx.fillStyle = '#e9c46a';
    for (let y = 20; y < h - 16; y += 16) {
      for (let x = 12; x < w - 12; x += 14) {
        ctx.fillRect(x, y, 8, 10);
      }
    }
  });

  canvasTexture(scene, 'shop', 70, 70, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#e76f51';
    ctx.fillRect(6, 28, w - 12, h - 28);
    ctx.fillStyle = '#f4a261';
    ctx.beginPath();
    ctx.moveTo(2, 30);
    ctx.lineTo(w / 2, 8);
    ctx.lineTo(w - 2, 30);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#264653';
    ctx.fillRect(w / 2 - 10, h - 28, 20, 28);
  });

  canvasTexture(scene, 'tree', 40, 60, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#6f4518';
    ctx.fillRect(w / 2 - 4, h - 24, 8, 22);
    ctx.fillStyle = '#2d6a4f';
    ctx.beginPath();
    ctx.arc(w / 2, 24, 16, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#40916c';
    ctx.beginPath();
    ctx.arc(w / 2 - 6, 20, 10, 0, Math.PI * 2);
    ctx.fill();
  });

  canvasTexture(scene, 'particle', 8, 8, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = '#ffd166';
    ctx.beginPath();
    ctx.arc(w / 2, h / 2, 3, 0, Math.PI * 2);
    ctx.fill();
  });
}

/** @deprecated use generateFallbackTextures */
export const generateAllTextures = generateFallbackTextures;
